import React from 'react';

/**
 * Privacy Policy — mirrored from sparvi.io/privacy-policy so it can be shown
 * inline during signup without redirecting. Keep aligned with
 * sparvi.io/pages/privacy-policy.tsx when legal text changes.
 *
 * Cookie-settings links from the marketing version are converted to static
 * text in this in-app version since the cookie-settings dialog lives on
 * the marketing site only.
 *
 * Last updated: October 27, 2025
 */
const PrivacyContent = () => (
  <>
    <p><strong>Last Updated: October 27, 2025</strong></p>

    <h2>1. Introduction</h2>
    <p>
      Welcome to Sparvi LLC (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). We are committed to protecting your
      privacy and handling your data with transparency and care. This Privacy Policy
      explains how we collect, use, disclose, and safeguard your information when you use
      our website (sparvi.io), our collaborative data observability service, and related
      applications (collectively, the &quot;Service&quot;).
    </p>
    <p>
      Your privacy is important to us. This policy explains what data we collect, why we
      collect it, and how you can control your information.
    </p>

    <h2>2. Information We Collect</h2>

    <h3>2.1 Information You Provide to Us</h3>
    <ul>
      <li><strong>Account Information</strong>: When you register for an account, we collect information such as your name, email address, password, and company name.</li>
      <li><strong>Payment Information</strong>: If you subscribe to our paid services, we collect payment details, though credit card information is processed by our payment processors and not stored directly by us.</li>
      <li><strong>Profile Information</strong>: Information you provide in your user profile, such as your job title and profile picture.</li>
      <li><strong>Communications</strong>: Information you provide when you contact us for support, respond to surveys, or communicate with us in any way.</li>
      <li><strong>Service Data</strong>: Information you upload, generate, or provide when using our Service, including database connection details and data profiling results.</li>
    </ul>

    <h3>2.2 Information We Collect Automatically</h3>
    <p><strong>Website Analytics</strong></p>
    <p>
      When you visit our website, we use PostHog (a privacy-friendly analytics platform)
      to collect information about how you interact with our site. This helps us
      understand how people use Sparvi so we can improve your experience.
    </p>
    <p>PostHog collects:</p>
    <ul>
      <li>Pages you visit and features you use</li>
      <li>Buttons and links you click</li>
      <li>Time spent on pages</li>
      <li>Device type, browser, and operating system</li>
      <li>General location (country/city level, based on IP address)</li>
      <li>Referrer (how you arrived at our site)</li>
    </ul>
    <p>
      PostHog uses cookies to remember your session and track your activity. You can opt
      out of analytics tracking at any time using the Cookie Settings link on{' '}
      <a
        href="https://sparvi.io"
        target="_blank"
        rel="noopener noreferrer"
      >
        sparvi.io
      </a>.
    </p>

    <p><strong>Other Technical Information</strong></p>
    <ul>
      <li><strong>Log Data</strong>: Server logs, IP addresses, and timestamps for security and troubleshooting</li>
      <li>
        <strong>Cookies</strong>: Essential cookies for authentication and site functionality. See our{' '}
        <a
          href="https://sparvi.io/cookie-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Cookie Policy
        </a>{' '}
        for details.
      </li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <p>We use the information we collect to:</p>
    <ul>
      <li><strong>Provide and improve our Service</strong>: Understand how you use Sparvi so we can make it better</li>
      <li><strong>Process transactions</strong>: Handle payments and manage your subscription</li>
      <li><strong>Communicate with you</strong>: Send important updates, respond to support requests, and share product news (if you&apos;ve opted in)</li>
      <li><strong>Analyze usage</strong>: Use PostHog analytics to identify popular features, find bugs, and improve user experience</li>
      <li><strong>Keep our Service secure</strong>: Monitor for fraud, abuse, and technical issues</li>
      <li><strong>Comply with legal requirements</strong>: Meet our legal and regulatory obligations</li>
    </ul>

    <p><strong>What we don&apos;t do:</strong></p>
    <ul>
      <li>Sell your personal information to third parties</li>
      <li>Use your data for advertising or marketing without your consent</li>
      <li>Share your data with advertisers or data brokers</li>
    </ul>

    <h2>4. Information Sharing and Disclosure</h2>
    <p>We may share your information with:</p>

    <h3>4.1 Third-Party Service Providers</h3>
    <p>
      We work with trusted third-party service providers who help us operate our Service.
      These providers are contractually obligated to protect your information and can only
      use it for the specific purposes we authorize.
    </p>
    <p>Our service providers include:</p>
    <ul>
      <li>
        <strong>PostHog</strong> (Analytics): Helps us understand how you use our website. PostHog is
        privacy-focused and processes data securely.{' '}
        <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">
          PostHog Privacy Policy
        </a>
      </li>
      <li><strong>Supabase</strong> (Database hosting): Stores your account and service data securely</li>
      <li><strong>Azure</strong> (Cloud hosting): Hosts our website and application infrastructure</li>
      <li><strong>Payment processors</strong>: Process subscription payments (we don&apos;t store your credit card details)</li>
      <li><strong>Email service providers</strong>: Send transactional emails and support communications</li>
    </ul>

    <h3>4.2 Compliance with Laws</h3>
    <p>
      We may disclose your information if required to do so by law or in response to valid
      requests by public authorities.
    </p>

    <h3>4.3 Business Transfers</h3>
    <p>
      If we are involved in a merger, acquisition, or sale of all or a portion of our
      assets, your information may be transferred as part of that transaction.
    </p>

    <h3>4.4 With Your Consent</h3>
    <p>We may share your information with third parties when you explicitly consent to such sharing.</p>

    <h2>5. Data Security</h2>
    <p>
      We implement appropriate technical and organizational measures to protect your
      personal information against unauthorized access, accidental loss, alteration, or
      destruction. However, no method of transmission over the Internet or electronic
      storage is 100% secure, and we cannot guarantee absolute security.
    </p>

    <h2>6. Your Rights and Choices</h2>
    <p>You have control over your personal information. Here&apos;s what you can do:</p>

    <h3>6.1 Opt Out of Analytics Tracking</h3>
    <p>
      You can opt out of PostHog analytics at any time by visiting{' '}
      <a
        href="https://sparvi.io"
        target="_blank"
        rel="noopener noreferrer"
      >
        sparvi.io
      </a>{' '}
      and using the Cookie Settings link to disable analytics cookies. This will prevent
      us from tracking your website activity, though we&apos;ll still collect essential data
      needed for the site to function.
    </p>

    <h3>6.2 Your Data Rights</h3>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong>: Request a copy of the personal information we hold about you</li>
      <li><strong>Correction</strong>: Ask us to correct inaccurate or incomplete information</li>
      <li><strong>Deletion</strong>: Request that we delete your personal information (subject to legal obligations)</li>
      <li><strong>Portability</strong>: Receive your data in a common, machine-readable format</li>
      <li><strong>Objection</strong>: Object to our processing of your information for certain purposes</li>
    </ul>
    <p>
      To exercise these rights, please contact us at{' '}
      <a href="mailto:contact@sparvi.io">contact@sparvi.io</a>. We&apos;ll respond within 30 days.
    </p>

    <h3>6.3 California Privacy Rights (CCPA)</h3>
    <p>
      If you&apos;re a California resident, you have additional rights under the California
      Consumer Privacy Act (CCPA), including the right to know what personal information
      we collect, and the right to request deletion of your information. We do not sell
      your personal information.
    </p>

    <h2>7. Children&apos;s Privacy</h2>
    <p>
      Our Service is not directed to children under the age of 16. We do not knowingly
      collect personal information from children under 16. If you become aware that a
      child has provided us with personal information, please contact us.
    </p>

    <h2>8. Data Storage and Transfers</h2>
    <p>
      Our Service is primarily hosted in the United States using Microsoft Azure and
      Supabase infrastructure. If you access our Service from outside the United States,
      your information will be transferred to and stored in the US. By using our Service,
      you consent to this transfer.
    </p>

    <h2>9. Retention of Information</h2>
    <p>
      We retain your personal information for as long as necessary to fulfill the purposes
      outlined in this Privacy Policy, unless a longer retention period is required or
      permitted by law.
    </p>

    <h2>10. Updates to this Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. The updated version will be
      indicated by an updated &quot;Last Updated&quot; date. We encourage you to review this Privacy
      Policy periodically.
    </p>

    <h2>11. Contact Us</h2>
    <p>If you have questions, concerns, or want to exercise your privacy rights, please contact us:</p>
    <p>
      <strong>Sparvi LLC</strong>
      <br />
      <strong>Email</strong>:{' '}
      <a href="mailto:contact@sparvi.io">contact@sparvi.io</a>
    </p>
    <p>We&apos;ll do our best to respond within 2 business days.</p>
  </>
);

export default PrivacyContent;
